import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import Peer from 'peerjs'

import { UserContext } from '../context/user'
import { ReactComponent as ChatIcon } from '../assets/icons/chat.svg'
import { ReactComponent as ChatUnreadIcon } from '../assets/icons/chat_unread.svg'
import { ReactComponent as ArrowIcon } from '../assets/icons/arrow_right.svg'

type Data = {
	type: 'message'
	from: string
	content: string
	at: number
}

export default () => {
	const { state } = useContext(UserContext)

	const [connection, set_connection] = useState<Peer.DataConnection>(null)
	const [shown, set_shown] = useState(false)
	const [unread, set_unread] = useState(false)
	const [content, set_content] = useState('')
	const [messages, set_messages] = useState<Data[]>([])

	const {
		local: { init, id, peer },
		remote: { id: remote_id },
	} = state

	useEffect(() => {
		if (init) {
			const connection = peer.connect(remote_id, { serialization: 'json' })
			set_connection(connection)
			connection.on('data', (data) => receive(data))
		}
	}, [init, peer, remote_id]) // eslint-disable-line

	useEffect(() => {
		peer.on('connection', (connection) => {
			if (connection.peer !== remote_id) {
				connection.close()
				return
			}
			set_connection(connection)
			connection.on('data', (data) => receive(data))
		})
	}, [peer, remote_id]) // eslint-disable-line

	const send = ({ type, content }) => {
		const data: Data = { type, from: id, content, at: Date.now() }
		connection.send(data)
		if (data.type === 'message') set_messages((messages) => [...messages, data])
	}

	const receive = (data) => {
		if (data.type === 'message') {
			set_messages((messages) => [...messages, data])
			if (!shown) set_unread(true)
		}
	}

	const toggle_shown = () => {
		set_unread(false)
		set_shown(!shown)
	}

	const handle_change = ({ target: { value } }) => set_content(value)
	const handle_submit = (event) => {
		event.preventDefault()

		if (!!content.trim()) {
			send({ type: 'message', content })
			set_content('')
		}
	}

	return (
		<>
			<ChatButton onClick={toggle_shown}>
				{unread ? <ChatUnreadIcon /> : <ChatIcon />}
			</ChatButton>
			<Container shown={shown}>
				<Bar>
					<p></p>
					<ArrowButton onClick={toggle_shown}>
						<ArrowIcon />
					</ArrowButton>
				</Bar>
				<Messages>
					<Div>
						{messages.map(({ from, content, at }) => (
							<Message key={`${from}:${at}`} from={String(from === id)}>
								{content}
							</Message>
						))}
					</Div>
				</Messages>
				<Form onSubmit={handle_submit}>
					<Input
						placeholder="type message"
						value={content}
						onChange={handle_change}
					/>
					<Send>send</Send>
				</Form>
			</Container>
		</>
	)
}

const Button = styled.button`
	background: none;
	border: none;
	outline: none;
	cursor: pointer;
`
const ChatButton = styled(Button)`
	position: absolute;
	right: 1rem;
	top: 0.5rem;
	padding: 0.5rem;
	z-index: 2;
`
const Container = styled.section<{ shown }>`
	height: 100%;
	max-width: 20rem;
	position: fixed;
	top: 0;
	right: 0;
	background: ${({ theme: { colors } }) => colors.white};
	z-index: 4;
	display: flex;
	flex-direction: column;
	align-self: flex-end;
	overflow: hidden;
	border-left: 1px solid ${({ theme: { colors } }) => colors.grey[0]};
	transform: translateX(${({ shown }) => (shown ? '0' : '20rem')});
`
const Bar = styled.div`
	height: 2.5rem;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0.5rem 1rem;
	padding-left: calc(50% - 1.5rem);
	font-size: 0.8rem;
`
const ArrowButton = styled(Button)`
	margin-left: auto;
	display: flex;
	padding: 0.7rem;
`
const Div = styled.div`
	display: flex;
	flex-direction: column;
`
const Messages = styled.ul`
	height: calc(100% - 2.5rem);
	display: flex;
	overflow-y: scroll;
	flex-direction: column-reverse;
	list-style-type: none;
	scrollbar-width: none;
	margin: 0 1.5rem;

	::-webkit-scrollbar {
		display: none;
	}
`
const Message = styled.li<{ from? }>`
	max-width: 70%;
	margin: 0.3rem 0;
	padding: 0.5rem 0.8rem;
	font-size: 0.8rem;
	display: flex;
	word-wrap: break-word;
	word-break: break-word;
	align-self: ${({ from }) => (from === 'true' ? 'flex-end' : 'flex-start')};
	color: ${({ from, theme: { colors } }) =>
		from === 'true' ? colors.white : colors.grey[2]};
	background: ${({ from, theme: { colors } }) =>
		from === 'true' ? colors.primary : colors.grey[0]};
`
const Form = styled.form`
	background: white;
	bottom: 1rem;
	height: 2.8rem;
	display: flex;
	justify-content: space-between;
	padding-top: 0.6rem;
	margin: 0 1.5rem 1rem;
`
const Input = styled.input`
	width: 13rem;
	border: none;
	outline: none;
	font-size: 0.8rem;
	background: ${({ theme: { colors } }) => colors.grey[0]};
	padding: 0 0.8rem;
`
const Send = styled.button`
	width: 4rem;
	padding: 0 0.8rem;
	border: none;
	outline: none;
	font-size: 0.8rem;
	cursor: pointer;
	color: ${({ theme: { colors } }) => colors.white};
	background: ${({ theme: { colors } }) => colors.primary};

	:focus {
		text-decoration: underline;
	}
`
